import { clusterMaxZoom, getLayerId, RemovedTrees } from "./TreeMaps";

// A layer `maxzoom` hides the layer from that zoom on. The cluster layers use the zoom where the
// clustering itself stops: above it the source hands out raw points, so the circles are gone from
// the data - but mapbox kept drawing the old tile's *symbols* (the count numbers) until their fade
// ended, which left the numbers alone on screen for a second or two while the tree icons of the
// finer tile were already drawn. Taking the layers out of the painter from that zoom on keeps the
// number and the circle together.
const badgeMaxZoom = () => clusterMaxZoom + 1;

export const getSelectedClustersLayer = (id: string, source: string, treesForLayer: any[]) => {
  const layerId = getLayerId(id, source);
  const filter: any = [
    'all',
    ['has', 'point_count'],
    ['in', ['get', 'id'], ["literal", treesForLayer]]
  ];

  return {
    id: layerId,
    source,
    type: 'circle',
    filter,
    paint: {
      'circle-color': '#010691',
      'circle-radius': [
        'step',
        ['get', 'treeCount'],
        20, 100,
        30, 750,
        40,
      ],
      'circle-pitch-alignment': 'viewport',
      'circle-pitch-scale': 'viewport',
      'circle-opacity': 1,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 1,
    }
  }
}

export const getClustersLayer = (id: string, source: string, removedTrees?: RemovedTrees) => {
  const layerId = getLayerId(id, source);
  const treesForLayer = removedTrees && removedTrees[layerId] || [''];
  let filter: any = ['has', 'point_count'];

  if (removedTrees && removedTrees[layerId] && removedTrees[layerId].length > 0) {
    filter = [
      'all',
      filter,
      ['!', ['in', ['get', 'id'], ['literal', treesForLayer]]]
    ];
  }

  return {
    id: layerId,
    source,
    type: 'circle',
    maxzoom: badgeMaxZoom(),
    filter,
    paint: {
      // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
      // with three steps to implement three types of circles:
      //   * Blue, 20px circles when point count is less than 100
      //   * Yellow, 30px circles when point count is between 100 and 750
      //   * Pink, 40px circles when point count is greater than or equal to 750
      'circle-color': [
        'step',
        ['get', 'treeCount'],
        '#51bbd6',
        5, '#81f175',
        10, '#f1f075',
        100, '#f28cb1'
      ],
      'circle-radius': [
        'step',
        ['get', 'treeCount'],
        20, 100,
        30, 750,
        40,
      ],
      'circle-pitch-alignment': 'viewport',
      'circle-pitch-scale': 'viewport',
      'circle-opacity': 1,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 1,
    }
  }
}

export const getClusterCountLayer = (id: string, source: string, removedTrees?: RemovedTrees) => {
  const layerId = getLayerId(id, source);
  const treesForLayer = removedTrees && removedTrees[layerId] || [''];
  let filter: any = ['has', 'point_count'];

  if (removedTrees && removedTrees[layerId] && removedTrees[layerId].length > 0) {
    filter = ['all', filter, ['!', ['in', ['get', 'id'], treesForLayer]]];
  }

  return {
    id: getLayerId(id, source),
    source,
    type: 'symbol',
    maxzoom: badgeMaxZoom(),
    filter: filter,
    layout: {
      'text-field': [
        'number-format',
        ['get', 'treeCount'],
        { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }
      ],
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 12,
      'text-pitch-alignment': 'viewport',
    }
  }
}

export const getUnclusteredPointLayer = (id: string, source: string, removedTrees?: RemovedTrees) => {
  const layerId = getLayerId(id, source);
  const treesForLayer = removedTrees && removedTrees[layerId] || [''];
  let filter: any = ['!', ['has', 'point_count']];

  if (removedTrees && removedTrees[layerId] && removedTrees[layerId].length > 0) {
    filter = ['all', filter, ['match', ['get', 'id'], treesForLayer, false, true]];
  }

  return {
    id: layerId,
    source,
    type: 'symbol',
    filter,
    layout: {
      'text-field': [
        'number-format',
        ['get', 'numberPlanted'],
        { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }
      ],
      'text-size': 12,
      'icon-image': [
        'match',
        ['to-string', ['get', 'fruit_tree']], '1', 'tree-fruit', 'tree-shade',
      ],
      'icon-size': 0.5,
      'icon-pitch-alignment': 'viewport',
      'text-pitch-alignment': 'viewport',
      // Every tree is drawn, including the ones whose icons or numbers overlap each other. From
      // `clusterMaxZoom` up the badges are gone, so these icons are the only thing left to show the
      // trees; letting the collision engine drop the overlapping ones hid trees that are metres
      // apart. Same settings as the selected-tree layer below.
      'icon-allow-overlap': true,
      'text-allow-overlap': true,
      'icon-ignore-placement': true,
      'text-ignore-placement': true,
    },
  }
}

export const getSelectedUnclusteredPointLayer = (id: string, source: string) => {
  return {
    id: getLayerId(id, source),
    source,
    type: 'symbol',
    filter: ["!", ["has", "point_count"]],
    layout: {
      'text-field': [
        'number-format',
        ['get', 'numberPlanted'],
        { 'min-fraction-digits': 0, 'max-fraction-digits': 0 }
      ],
      'text-size': 12,
      'icon-image': [
        'match',
        ['to-string', ['get', 'fruit_tree']], '1', 'tree-fruit', 'tree-shade',
      ],
      'icon-size': 0.5,
      'icon-pitch-alignment': 'viewport',
      'text-pitch-alignment': 'viewport',
      'icon-allow-overlap': true,
      'text-allow-overlap': true,
      'icon-ignore-placement': true,
      'text-ignore-placement': true,
    },
    paint: {
      'icon-opacity': source === 'selected-tree' ? 1 : ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0],
      'text-opacity': source === 'selected-tree' ? 1 : ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0],
    }
  }
}
export const getSelectionShadowLayer = (id: string, source: string) => {
  return {
    id: getLayerId(id + '-shadow', source),
    source,
    type: 'circle',
    filter: ["!", ["has", "point_count"]],
    paint: {
      'circle-radius': source === 'selected-tree' ? 18 : ['case', ['boolean', ['feature-state', 'selected'], false], 18, 0],
      'circle-color': '#007AFF', // Professional Blue
      'circle-blur': 0.8,
      'circle-opacity': source === 'selected-tree' ? 0.6 : ['case', ['boolean', ['feature-state', 'selected'], false], 0.6, 0]
    }
  }
}
